import 'react-circular-progressbar/dist/styles.css';

import { Loading } from '@nextui-org/react';
import React, { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { TrashIcon } from '../../assets';
import { ResendIcon } from '../../assets';
import { FailedIcon, FileCheckIcon, FileChooseIcon, FileTypeFailedIcon } from '../../assets/FileChooser';
import { StorageLevel, uploadFile, UploadFileConfig, UploadProgress } from '../../utils/Amplify';
import CircularProgress from '../CircularProgress';
import {
  ActionContainer,
  ActionLink,
  Card,
  ClickableLink,
  Container,
  FileIconContainer,
  Filename,
  FilenameContainer,
  IconContainer,
  Subtitle,
  Title,
  TitleContainer,
} from './FileChooser.styles';

interface FileChooserProps {
  accept?: string;
  iconFile?: ReactNode;
  file?: File;
  onFileSelect?: (file: File) => void;
  onFileDelete?: () => void;
  fileUrl: string | undefined;
  disableUpload?: boolean;
  title?: string;
  chooseIcon?: ReactNode;
  chooseText?: (onBrowse: () => void) => ReactNode;
  storageLevel?: StorageLevel;
  propsUpload?: {
    config?: UploadFileConfig;
    onUploadStart?: () => void;
    onUploadFinish?: (url: string) => void;
    onUploadProgress?: (percentage: number) => void;
  };
  isLoading?: boolean;
}

const FileChooser = forwardRef(
  (
    {
      accept,
      iconFile,
      file,
      onFileSelect,
      disableUpload,
      propsUpload,
      chooseIcon,
      chooseText,
      title,
      storageLevel = StorageLevel.PUBLIC,
      isLoading = false,
    }: FileChooserProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>();
    const [isDragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File>(file);
    const uploadOperationRef = useRef(undefined);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [isUploadFailed, setUploadFailed] = useState(false);
    const [completedPercentage, setCompletedPercentage] = useState(0);

    // Sync selectedFile with file prop
    useEffect(() => {
      setSelectedFile(file);
    }, [file]);

    const progressCallback = (progress: UploadProgress) => {
      const percentage = Math.trunc((progress.loaded * 100) / progress.total);
      setCompletedPercentage(percentage);
      propsUpload?.onUploadProgress?.(percentage);
    };

    const handleDrag = (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      if (selectedFile) return;

      if (accept) {
        const exts = accept.split(',');
        const extension: string = event.dataTransfer.items[0]?.type?.split('/').pop();
        if (extension && !exts.includes('.' + extension)) return;
      }

      if (event.type === 'dragenter' || event.type === 'dragover') {
        setDragActive(true);
      } else if (event.type === 'dragleave') {
        setDragActive(false);
      }
    };

    const onUploadFinish = (url: string) => {
      propsUpload?.onUploadFinish(url);
    };

    const selectFile = (file?: File) => {
      setSelectedFile(file);
      onFileSelect?.(file);

      if (!disableUpload && file) {
        void uploadSelectedFile(file);
      }
    };

    const uploadSelectedFile = async (file: File) => {
      setUploadingFile?.(true);
      setUploadFailed(false);
      propsUpload?.onUploadStart?.();

      try {
        const url = await uploadFile(file, progressCallback, uploadOperationRef, propsUpload?.config, storageLevel);
        if (url) {
          onUploadFinish(url);
        }
      } catch (error: any) {
        onUploadFinish(undefined);
        setUploadFailed(true);
      } finally {
        setUploadingFile?.(false);
        setCompletedPercentage(0);
      }
    };

    const handleDrop = (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isDragActive) return;
      setDragActive(false);
      selectFile(event.dataTransfer.files[0] as File);
    };

    const onBrowse = () => {
      inputRef.current.click();
    };

    const onFileChange = (event: any) => {
      event.preventDefault();
      selectFile(event.target.files[0] as File);
    };

    const onFileDelete = () => {
      selectFile(undefined);
      inputRef.current.value = '';
    };

    useImperativeHandle(ref, () => ({
      retryUpload: onRetryUpload,
    }));

    const onRetryUpload = () => {
      void uploadSelectedFile(selectedFile);
    };

    const renderLoadingView = () => (
      <FilenameContainer>
        <Filename>
          {iconFile && <IconContainer>{iconFile}</IconContainer>}
          <Subtitle $isFileSelected>{t('components.fileChooser.loadingFile')}</Subtitle>
        </Filename>
        <Loading />
      </FilenameContainer>
    );

    const renderSelectedFileView = () => (
      <FilenameContainer>
        <Filename>
          {iconFile && (
            <IconContainer $failed={isUploadFailed}>{isUploadFailed ? <FileTypeFailedIcon /> : iconFile}</IconContainer>
          )}
          <Subtitle $isFileSelected>{selectedFile?.name}</Subtitle>
        </Filename>
        {uploadingFile ? (
          <CircularProgress size={28} value={completedPercentage} />
        ) : isUploadFailed ? (
          <FailedIcon />
        ) : (
          <FileCheckIcon />
        )}
      </FilenameContainer>
    );

    const renderEmptyView = () => (
      <FileIconContainer>
        {chooseIcon || <FileChooseIcon />}
        <Subtitle>
          {chooseText?.(onBrowse) || (
            <Trans
              i18nKey={'components.fileChooser.infoText'}
              components={{ span: <ClickableLink onClick={onBrowse} /> }}
            />
          )}
        </Subtitle>
      </FileIconContainer>
    );

    const renderContent = () => {
      if (isLoading) return renderLoadingView();
      if (selectedFile) return renderSelectedFileView();
      return renderEmptyView();
    };

    return (
      <Container>
        <TitleContainer>
          <Title>{title || t('components.fileChooser.uploadedTitle')}</Title>
          {!uploadingFile && selectedFile && (
            <ActionContainer>
              {isUploadFailed ? <ResendIcon /> : <TrashIcon />}
              <ActionLink onClick={isUploadFailed ? onRetryUpload : onFileDelete}>
                {t(`components.fileChooser.${isUploadFailed ? 'retry' : 'deleteFile'}`)}
              </ActionLink>
            </ActionContainer>
          )}
        </TitleContainer>
        <Card
          $isDragging={isDragActive}
          $isFileSelected={!!selectedFile}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {renderContent()}
          <input type="file" onChange={onFileChange} hidden ref={inputRef} accept={accept} />
        </Card>
      </Container>
    );
  },
);

export default FileChooser;
